<svelte:options
  customElement={{
    tag: "oc-icon-button-v1",
    /*                                                         */
    /*         */
    extend: (CustomElementConstructor) =>
      class extends CustomElementConstructor {
        constructor() {
          super();
          this.host = this;
          this.internals = this.attachInternals();
        }
      },
    props: {
      aria: { type: "Object" },
      elevated: { type: "Boolean" },
      transparent: { type: "Boolean" },
      disabled: { type: "Boolean", reflect: true },
      iconType: { type: "String", attribute: "icon-type" },
      size: { type: "String", reflect: true },
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import type { Props } from "./IconButtonV1.types.js";
  import type { CommonProps } from "../../../common/component.types.js";
  import { useAriaAttributes } from "../../../common/utils/aria/useAriaAttributes.js";
  import { applyAttributesToHost } from "../../../common/utils/aria/applyAttributesToHost.js";

  export let aria: Required<CommonProps>["aria"] = {};
  $: {
    const ariaAttributes = useAriaAttributes(aria);
    applyAttributesToHost(ariaAttributes.default);
  }

  /**
 *
 *
 */
  export let elevated: Props["elevated"] = false;
  /**
 *
 *
 */
  export let transparent: Props["transparent"] = false;
  /**
 *
 *
 */
  export let disabled: Props["disabled"] = false;
  /**
 *
 */
  export let iconType: Props["iconType"];
  /**
 *
 *
 */
  export let size: Props["size"] = "50";

  export let host: HTMLElement;

  let iconButton: HTMLDivElement;

  const onIconButtonClickEnter = ({ code }: KeyboardEvent) => {
    if (disabled) {
      return;
    }

    if (code === "Space" || code === "Enter") {
      iconButton?.classList.add("icon-button--active");
      aria.pressed = "true";
    }
  };

  const onIconButtonClickLeave = ({ code }: KeyboardEvent) => {
    if (disabled) {
      return;
    }

    if (code === "Space" || code === "Enter") {
      iconButton?.classList.remove("icon-button--active");
      aria.pressed = undefined;
    }
  };

  onMount(() => {
    host.role = "button";

    if (!host.hasAttribute("tabindex")) {
      host.tabIndex = 0;
    }

    host.addEventListener("keydown", onIconButtonClickEnter);
    host.addEventListener("keyup", onIconButtonClickLeave);
  });
</script>

<!-- eslint-disable no-nested-ternary -->
<div
  bind:this={iconButton}
  class="icon-button {`icon-button--size-${size}`}"
  class:icon-button--disabled={disabled}
  class:icon-button--elevated={size === "50" && !transparent
    ? elevated
    : false || (size === "100" && !transparent)
      ? elevated
      : false}
  class:icon-button--transparent={transparent}
>
  <oc-icon-v1
    aria={{ hidden: "true" }}
    class="icon-button__icon"
    class:icon-button__icon--disabled={disabled}
    size={"100"}
    type={iconType}
  />
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "./IconButtonV1.config" as config;
  @use "../../../common/scss/focus.scss";
  @use "../../../common/scss/visually-hidden.scss" as *;

  :host {
    @include config.oc-icon-button;
    outline: none;
    position: relative;
  }

  :host(:hover:not(:host[disabled])) {
    .icon-button {
      cursor: pointer;
      background-color: tokens.$oc-component-icon-button-default-background-color-hover;
    }
  }

  :host(:active:not(:host[disabled])) {
    .icon-button {
      cursor: pointer;
      background-color: tokens.$oc-component-icon-button-default-background-color-active;
    }
  }

  :host(:focus) {
    &:after {
      $offset: calc(-1 * (tokens.$oc-semantic-focus-outline-offset));
      content: "";
      display: block;
      position: absolute;
      left: $offset;
      top: $offset;
      bottom: $offset;
      right: $offset;
      outline: tokens.$oc-semantic-focus-outline-color tokens.$oc-semantic-focus-outline-style
        tokens.$oc-semantic-focus-outline-width;
      border-radius: 50%;
    }
  }

  @supports selector(*:focus-visible) {
    :host(:focus) {
      &:after {
        all: unset;
      }
    }

    :host(:focus-visible) {
      &:after {
        $offset: calc(-1 * (tokens.$oc-semantic-focus-outline-offset));
        content: "";
        display: block;
        position: absolute;
        left: $offset;
        top: $offset;
        bottom: $offset;
        right: $offset;
        outline: tokens.$oc-semantic-focus-outline-color tokens.$oc-semantic-focus-outline-style
          tokens.$oc-semantic-focus-outline-width;
        border-radius: 50%;
      }
    }
  }

  :host([size="50"]) {
    @include config.oc-icon-button-size-50;
  }

  :host([size="100"]) {
    @include config.oc-icon-button-size-100;
  }

  /*                 */
  .icon-button {
    position: relative;
    background-color: tokens.$oc-component-icon-button-default-background-color;
    border-radius: 50%;
    border: none;
    height: 32px;
    width: 32px;

    @include focus.styles();

    & > &-label {
      @include visually-hidden();
    }

    /*                                                */
    &:before {
      content: "";
      position: absolute;
      width: 48px;
      height: 48px;
      top: -8px;
      left: -8px;
      background-color: transparent;
    }

    /*                                                             */
    &--size-100 {
      height: 48px;
      width: 48px;

      &:before {
        display: none;
      }
    }

    &--transparent {
      background-color: tokens.$oc-component-icon-button-transparent-background-color;

      &:hover:enabled {
        background-color: tokens.$oc-component-icon-button-transparent-background-color-hover;
      }

      &:active:enabled {
        background-color: tokens.$oc-component-icon-button-transparent-background-color-active;
      }
    }

    &--elevated {
      &:not(.icon-button--transparent) {
        filter: tokens.$oc-base-shadow-200;
      }
    }

    &--active {
      cursor: pointer;
      background-color: tokens.$oc-component-icon-button-default-background-color-active;
    }

    &__icon {
      fill: #222222;
      left: 50%;
      top: 50%;
      position: absolute;
      transform: translate(-50%, -50%);

      &--disabled {
        cursor: not-allowed;
        fill: tokens.$oc-component-icon-button-disabled-icon-color;
      }
    }
  }
</style>
